import { useEffect, useMemo, useState } from "react";
import Layout from "../components/Layout";
import AdminDebugPanel from "../components/AdminDebugPanel";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Save, Share2, Plus, X, Copy, Check, Sparkles, Network, Link2 } from "lucide-react";
import { BiometricIcon, detectBiometricPlatform } from "../lib/biometrics";
import { Link } from "react-router-dom";
import { passkeysSupported, registerPasskey, listPasskeys, deletePasskey } from "../lib/webauthn";
import { InviteManager } from "../components/InviteManager";
import { Bell } from "lucide-react";
import {
  pushSupported, enablePush, disablePush, isSubscribed, notificationPermission, sendTestPush,
} from "../lib/push";

function NotificationSection() {
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const perm = notificationPermission();

  const refresh = () => isSubscribed().then(setSubscribed).catch(() => {});
  useEffect(() => { refresh(); }, []);

  const onEnable = async () => {
    setBusy(true);
    try {
      await enablePush();
      toast.success("Follow-up reminders enabled on this device");
      refresh();
    } catch (e) {
      toast.error(e?.message || "Could not enable notifications");
    } finally { setBusy(false); }
  };

  const onDisable = async () => {
    setBusy(true);
    try {
      await disablePush();
      toast.success("Reminders turned off on this device");
      refresh();
    } catch {
      toast.error("Could not turn off notifications");
    } finally { setBusy(false); }
  };

  const onTest = async () => {
    try {
      const r = await sendTestPush();
      if (r.sent > 0) toast.success("Test notification sent");
      else toast.error("No active subscription to send to");
    } catch {
      toast.error("Could not send test notification");
    }
  };

  if (!pushSupported()) return null;
  const denied = perm === "denied";
  return (
    <div className="mt-4 pt-4 border-t border-paper-100" data-testid="notification-section">
      <div className="flex items-start justify-between gap-3">
        <span className="text-sm text-ink-900 flex-1">
          <span className="font-medium flex items-center gap-2"><Bell className="w-4 h-4 text-sage-600" /> Follow-up reminders</span>
          <span className="block text-xs text-ink-500 mt-1">
            Get a private push reminder when you&apos;ve finished treatment but haven&apos;t completed a client&apos;s recommendations — a first nudge after 60 minutes and one end-of-day reminder. No client details ever appear on the notification. Reminders are per device.
          </span>
          {denied && (
            <span className="block text-xs text-terracotta-600 mt-2">
              Notifications are blocked in your browser settings. Re-enable them for this site to turn reminders on.
            </span>
          )}
        </span>
        {subscribed ? (
          <button
            type="button"
            onClick={onDisable}
            disabled={busy}
            data-testid="notifications-disable-btn"
            className="min-h-[40px] px-4 rounded-full border border-paper-200 hover:bg-paper-100 disabled:opacity-50 text-ink-700 text-sm font-medium inline-flex items-center gap-2 shrink-0 transition-all"
          >
            {busy ? "Turning off…" : "Turn off"}
          </button>
        ) : (
          <button
            type="button"
            onClick={onEnable}
            disabled={busy || denied}
            data-testid="notifications-enable-btn"
            className="min-h-[40px] px-4 rounded-full bg-ink-900 hover:bg-ink-900/90 disabled:opacity-50 text-white text-sm font-medium inline-flex items-center gap-2 shrink-0 transition-all"
          >
            <Bell className="w-4 h-4" />{busy ? "Enabling…" : "Enable reminders"}
          </button>
        )}
      </div>
      {subscribed && (
        <button
          type="button"
          onClick={onTest}
          data-testid="notifications-test-btn"
          className="mt-3 text-xs text-sage-700 hover:text-sage-800 underline underline-offset-2"
        >
          Send a test notification
        </button>
      )}
    </div>
  );
}

function PasskeySection() {
  const [creds, setCreds] = useState([]);
  const [domain, setDomain] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => listPasskeys().then((d) => { setCreds(d.credentials || []); setDomain(d.this_domain); }).catch(() => {});
  useEffect(() => { load(); }, []);

  const onRegister = async () => {
    setBusy(true);
    try {
      await registerPasskey();
      toast.success("Biometric sign-in enabled on this device");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Could not register passkey");
    } finally {
      setBusy(false);
    }
  };

  const onRemove = async (cid) => {
    try {
      await deletePasskey(cid);
      toast.success("Passkey removed");
      load();
    } catch {
      toast.error("Could not remove passkey");
    }
  };

  if (!passkeysSupported()) return null;
  const thisDomainCreds = creds.filter((c) => c.rp_id === domain);
  return (
    <div className="mt-4 pt-4 border-t border-paper-100" data-testid="passkey-section">
      <div className="flex items-start justify-between gap-3">
        <span className="text-sm text-ink-900 flex-1">
          <span className="font-medium">Biometric sign-in &amp; unlock</span>
          <span className="block text-xs text-ink-500 mt-1">
            Use your device&apos;s biometrics — Face ID, Touch ID, Android face or fingerprint unlock, or Windows Hello — instead of typing your password, on the lock screen and at sign-in. Your device handles verification; Posturra never sees or stores biometric data. Passkeys are stored per device and per domain.
          </span>
        </span>
        <button
          type="button"
          onClick={onRegister}
          disabled={busy}
          data-testid="register-passkey-btn"
          className="min-h-[40px] px-4 rounded-full bg-ink-900 hover:bg-ink-900/90 disabled:opacity-50 text-white text-sm font-medium inline-flex items-center gap-2 shrink-0 transition-all"
        >
          <BiometricIcon className="w-4 h-4" />
          {busy ? "Registering…" : thisDomainCreds.length ? "Add another device" : `Enable ${detectBiometricPlatform().method}`}
        </button>
      </div>
      {creds.length > 0 && (
        <ul className="mt-3 space-y-2" data-testid="passkey-list">
          {creds.map((c) => (
            <li key={c.credential_id} className="flex items-center justify-between gap-3 text-xs text-ink-700 bg-paper-50 border border-paper-200 rounded-2xl px-3 py-2">
              <span>
                Passkey on <span className="font-medium">{c.rp_id}</span> · added {new Date(c.created_at).toLocaleDateString()}
                {c.last_used_at && ` · last used ${new Date(c.last_used_at).toLocaleDateString()}`}
              </span>
              <button
                type="button"
                onClick={() => onRemove(c.credential_id)}
                data-testid={`remove-passkey-${c.credential_id}`}
                className="text-ink-500 hover:text-destructive underline underline-offset-2"
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
const FIELDS = [
  { key: "clinic_name", label: "Clinic / Practice Name", placeholder: "Posturra Wellness Studio" },
  { key: "license_type", label: "License Type", placeholder: "LMT / LMP / RMT" },
  { key: "license_number", label: "License Number", placeholder: "12345" },
  { key: "npi_number", label: "NPI Number", placeholder: "1234567890" },
  { key: "practice_address", label: "Practice Address", placeholder: "100 Main St, City, ST 00000", multiline: true },
  { key: "practice_phone", label: "Phone", placeholder: "(555) 555-1234" },
  { key: "practice_email", label: "Practice Email", placeholder: "you@yourclinic.com" },
  { key: "signature_name", label: "Signature Name (for reports)", placeholder: "First Last, LMT" },
];

const MODALITY_PRESETS = [
  "LightForce 45W therapy laser",
  "Hot stones",
  "Warm towels",
  "Cupping",
  "Gua sha / IASTM",
  "Cold stones",
  "Aromatherapy",
  "Kinesiology taping",
  "TheraGun / percussion",
  "Pin and stretch",
  "Cranial sacral",
];

export default function Settings() {
  const { user, refreshUser } = useAuth();
  const [form, setForm] = useState({});
  const [modalities, setModalities] = useState([]);
  const [preferredPositions, setPreferredPositions] = useState([]);
  const [newMod, setNewMod] = useState("");
  const [saving, setSaving] = useState(false);
  const [me, setMe] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => { localStorage.setItem("pstr_visited_settings", "1"); }, []);

  useEffect(() => {
    api.get("/auth/me").then((r) => {
      setForm(r.data.profile || {});
      setModalities(r.data.profile?.modalities || []);
      setPreferredPositions(r.data.profile?.preferred_positions || []);
      setMe(r.data);
    });
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch("/auth/profile", { ...form, modalities, preferred_positions: preferredPositions });
      // Refresh global user so modality chips appear in NewSession / AI prompts immediately
      await refreshUser();
      toast.success("Profile saved");
    } catch {
      toast.error("Could not save");
    } finally { setSaving(false); }
  };

  const addModality = (m) => {
    const v = (m || newMod).trim();
    if (!v || modalities.includes(v)) return;
    setModalities([...modalities, v]);
    setNewMod("");
  };
  const removeModality = (m) => setModalities(modalities.filter((x) => x !== m));

  const POSITION_OPTIONS = [
    { key: "supine", label: "Supine" },
    { key: "prone", label: "Prone" },
    { key: "side-lying", label: "Side-Lying" },
    { key: "seated", label: "Seated" },
  ];
  const togglePosition = (key) =>
    setPreferredPositions((prev) => (prev.includes(key) ? prev.filter((x) => x !== key) : [...prev, key]));

  const referralLink = me?.referral_code
    ? `${window.location.origin}/login?ref=${me.referral_code}`
    : "";

  const copyLink = () => {
    if (!referralLink) return;
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
    toast.success("Invite link copied");
  };

  const shareMessage = encodeURIComponent(
    `I've been using Posturra to do AI posture analysis, SOAP notes, and clinical reports for my massage clients. It's saved me hours per week — try it: ${referralLink}`
  );

  const availablePresets = useMemo(
    () => MODALITY_PRESETS.filter((m) => !modalities.includes(m)),
    [modalities]
  );

  return (
    <Layout>
      <div className="label-overline mb-2">Practice settings</div>
      <h1 className="font-heading text-4xl sm:text-5xl font-light tracking-tight text-ink-900 mb-2">Your credentials</h1>
      <p className="text-ink-700 mb-10 max-w-2xl">
        This information appears on the header of every clinical report — required for LOP, attorney correspondence, and insurance billing.
      </p>

      {/* Admin-only — Account & deployment debug panel */}
      {user?.is_admin && (
        <div className="mb-10">
          <AdminDebugPanel />
        </div>
      )}

      <form onSubmit={save} className="space-y-6 max-w-3xl">
        {/* Credentials */}
        <div className="bg-white border border-paper-200 rounded-3xl p-6 sm:p-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {FIELDS.map((f) => (
              <div key={f.key} className={f.multiline ? "sm:col-span-2" : ""}>
                <label className="label-overline block mb-2">{f.label}</label>
                {f.multiline ? (
                  <textarea
                    data-testid={`profile-${f.key}`}
                    rows={2}
                    value={form[f.key] || ""}
                    onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl bg-paper-50 border border-paper-200 focus:ring-2 focus:ring-sage-500/30 focus:border-sage-500 outline-none resize-none"
                    placeholder={f.placeholder}
                  />
                ) : (
                  <input
                    data-testid={`profile-${f.key}`}
                    value={form[f.key] || ""}
                    onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                    className="w-full min-h-[48px] px-4 rounded-xl bg-paper-50 border border-paper-200 focus:ring-2 focus:ring-sage-500/30 focus:border-sage-500 outline-none"
                    placeholder={f.placeholder}
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Draft hygiene */}
        <div className="bg-white border border-paper-200 rounded-3xl p-6 sm:p-8">
          <div className="label-overline mb-2">Draft sessions</div>
          <p className="text-sm text-ink-700 mb-5 max-w-2xl">
            Half-finished session drafts automatically move to the &ldquo;Archived drafts&rdquo; section after this many days. You can always restore an archived draft.
          </p>
          <label className="flex items-center gap-3 cursor-pointer select-none mb-4">
            <input
              type="checkbox"
              data-testid="draft-expiration-toggle"
              checked={form.draft_expiration_enabled !== false}
              onChange={(e) => setForm({ ...form, draft_expiration_enabled: e.target.checked })}
              className="w-4 h-4 rounded border-paper-200 text-sage-500 focus:ring-sage-500/30"
            />
            <span className="text-sm text-ink-900">Auto-archive expired drafts</span>
          </label>
          <div className="flex items-center gap-3">
            <label className="label-overline">Archive after</label>
            <input
              type="number"
              data-testid="draft-expiration-days"
              value={form.draft_expiration_days || 30}
              onChange={(e) => setForm({ ...form, draft_expiration_days: Math.max(1, parseInt(e.target.value) || 30) })}
              disabled={form.draft_expiration_enabled === false}
              min={1}
              max={365}
              className="w-24 min-h-[40px] px-3 rounded-lg bg-paper-50 border border-paper-200 focus:ring-2 focus:ring-sage-500/30 focus:border-sage-500 outline-none disabled:opacity-50"
            />
            <span className="text-sm text-ink-700">days</span>
          </div>
        </div>

        {/* Treatment Modalities */}
        <div className="bg-white border border-paper-200 rounded-3xl p-6 sm:p-8">
          <div className="flex items-center gap-2 mb-2">
            <Network className="w-4 h-4 text-sage-600" />
            <div className="label-overline text-sage-700">Clinical Outcomes Network</div>
          </div>
          <p className="text-sm text-ink-700 mb-4 max-w-2xl">
            Contribute anonymized protocols (steps, treatment-map muscles, modalities) and outcomes to the community library.
            Your name and clients are never shared — only your <b>community handle</b>. You can turn this off at any time.
          </p>
          <label className="flex items-center gap-3 cursor-pointer select-none mb-4">
            <input
              type="checkbox"
              data-testid="settings-community-toggle"
              checked={form.community_contribute !== false}
              onChange={(e) => setForm({ ...form, community_contribute: e.target.checked })}
              className="w-4 h-4 rounded border-paper-200 text-sage-500 focus:ring-sage-500/30"
            />
            <span className="text-sm text-ink-900">Contribute anonymized protocols & outcomes to the community</span>
          </label>
          <div>
            <label className="label-overline block mb-2">Community handle</label>
            <input
              value={form.community_display_name || ""}
              onChange={(e) => setForm({ ...form, community_display_name: e.target.value })}
              data-testid="settings-community-handle"
              placeholder={`Therapist #${(user?.id || "").slice(0, 6) || "abc123"}`}
              className="w-full max-w-md min-h-[44px] px-4 rounded-xl bg-paper-50 border border-paper-200 focus:ring-2 focus:ring-sage-500/30 focus:border-sage-500 outline-none"
            />
            <p className="text-xs text-ink-500 mt-2">Shown publicly next to your shared protocols and ratings. Leave blank for the default.</p>
          </div>
        </div>

        {/* Integrations */}
        <div className="bg-white border border-paper-200 rounded-3xl p-6 sm:p-8">
          <div className="flex items-center gap-2 mb-2">
            <Link2 className="w-4 h-4 text-sage-600" />
            <div className="label-overline text-sage-700">Integrations</div>
          </div>
          <p className="text-sm text-ink-700 mb-4 max-w-2xl">
            Connect external tools so intake data flows automatically into Posturra.
          </p>
          <Link
            to="/integrations/jotform"
            data-testid="settings-jotform-link"
            className="inline-flex items-center gap-2 min-h-[44px] px-5 rounded-full border border-paper-200 hover:bg-paper-100 text-ink-900 text-sm"
          >
            <Link2 className="w-4 h-4" /> Jotform intake forms
          </Link>
        </div>

        {/* Iter AF — Workflow automation */}
        <div className="bg-white border border-paper-200 rounded-3xl p-6 sm:p-8" data-testid="settings-workflow-card">
          <div className="flex items-center gap-2 mb-2">
            <Network className="w-4 h-4 text-sage-600" />
            <div className="label-overline text-sage-700">Workflow automation</div>
          </div>
          <p className="text-sm text-ink-700 mb-4 max-w-2xl">
            Internal AI helpers that fire automatically — they speed up your in-room flow without taking the client-facing message out of your hands. Anything client-facing (Send to Client, public portal links) always requires your explicit review and approval.
          </p>
          <label className="flex items-start gap-3 cursor-pointer select-none">
            <input
              type="checkbox"
              data-testid="settings-signature-capture-toggle"
              checked={form.signature_capture_enabled === true}
              onChange={(e) => setForm({ ...form, signature_capture_enabled: e.target.checked })}
              className="w-4 h-4 mt-1 rounded border-paper-200 text-sage-500 focus:ring-sage-500/30"
            />
            <span className="text-sm text-ink-900">
              <span className="font-medium">Enable patient signature capture</span> on the Review &amp; Save step.
              <span className="block text-xs text-ink-500 mt-1">Off by default. When on, the signature pad appears on the final review step so clients can sign on your device. Existing saved signatures are preserved either way.</span>
            </span>
          </label>

          <label className="flex items-start gap-3 select-none mt-4 pt-4 border-t border-paper-100">
            <span className="text-sm text-ink-900 flex-1">
              <span className="font-medium">Documentation grace period</span> after a treatment session ends.
              <span className="block text-xs text-ink-500 mt-1">The app stays unlocked for the session length plus this many minutes, then locks to protect client information. Default 15.</span>
            </span>
            <input
              type="number"
              min={5}
              max={60}
              data-testid="settings-lock-grace-input"
              value={form.lock_grace_minutes ?? 15}
              onChange={(e) => setForm({ ...form, lock_grace_minutes: Math.max(5, Math.min(60, parseInt(e.target.value || "15", 10))) })}
              className="w-20 min-h-[44px] px-3 rounded-2xl bg-white border border-paper-200 text-sm text-center"
            />
          </label>

          <PasskeySection />
          <NotificationSection />
        </div>

        {user?.is_admin && <InviteManager />}

        <div className="bg-white border border-paper-200 rounded-3xl p-6 sm:p-8">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-4 h-4 text-terracotta-500" />
            <div className="label-overline text-terracotta-600">Treatment modalities</div>
          </div>
          <p className="text-sm text-ink-700 mb-5 max-w-2xl">
            What equipment and techniques do you use? The AI will reference these when suggesting protocols and drafting SOAP notes (e.g., &ldquo;Used LightForce 45W therapy laser on the upper trap attachments for 5 minutes.&rdquo;).
          </p>
          <div className="flex flex-wrap gap-2 mb-5" data-testid="modality-chips">
            {modalities.length === 0 ? (
              <div className="text-sm text-ink-500">No modalities yet — add some below.</div>
            ) : modalities.map((m) => (
              <span key={m} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-sage-500 text-white text-sm" data-testid={`modality-${m}`}>
                {m}
                <button type="button" onClick={() => removeModality(m)} className="hover:opacity-70"><X className="w-3 h-3" /></button>
              </span>
            ))}
          </div>

          <div className="flex gap-2 mb-4">
            <input
              data-testid="new-modality-input"
              value={newMod}
              onChange={(e) => setNewMod(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addModality(); } }}
              placeholder="Add a custom modality"
              className="flex-1 min-h-[44px] px-4 rounded-xl bg-paper-50 border border-paper-200 focus:ring-2 focus:ring-sage-500/30 focus:border-sage-500 outline-none"
            />
            <button type="button" data-testid="add-modality-btn" onClick={() => addModality()} className="min-h-[44px] px-5 rounded-full bg-ink-900 hover:bg-ink-700 text-white text-sm inline-flex items-center gap-2">
              <Plus className="w-4 h-4" /> Add
            </button>
          </div>

          <div className="label-overline mb-2">Common modalities (tap to add)</div>
          <div className="flex flex-wrap gap-2">
            {availablePresets.map((m) => (
              <button
                key={m}
                type="button"
                data-testid={`preset-${m}`}
                onClick={() => addModality(m)}
                className="min-h-[36px] px-3 rounded-full border border-paper-200 hover:border-sage-500/40 hover:bg-paper-100 text-xs text-ink-700 transition-all"
              >
                + {m}
              </button>
            ))}
          </div>
        </div>

        {/* Treatment Positions (therapist routine — preference, not a contraindication) */}
        <div className="bg-white border border-paper-200 rounded-3xl p-6 sm:p-8" data-testid="treatment-positions-card">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-4 h-4 text-sage-500" />
            <div className="label-overline text-sage-700">Treatment positions</div>
          </div>
          <p className="text-sm text-ink-700 mb-5 max-w-2xl">
            Which client positions do you routinely use? Posturra will prefer these when clinically appropriate. This is a
            preference, not a clinical contraindication — client safety, tolerance, and access always take priority.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="position-options">
            {POSITION_OPTIONS.map((p) => {
              const on = preferredPositions.includes(p.key);
              return (
                <button
                  key={p.key}
                  type="button"
                  data-testid={`position-toggle-${p.key}`}
                  aria-pressed={on}
                  onClick={() => togglePosition(p.key)}
                  className={`min-h-[44px] px-4 rounded-xl border text-sm inline-flex items-center justify-center gap-2 transition-all ${
                    on ? "bg-sage-500 border-sage-500 text-white" : "bg-paper-50 border-paper-200 text-ink-700 hover:bg-paper-100"
                  }`}
                >
                  <span className={`inline-flex items-center justify-center w-4 h-4 rounded ${on ? "bg-white/25" : "border border-paper-300"}`}>
                    {on && <Check className="w-3 h-3" />}
                  </span>
                  {p.label}
                </button>
              );
            })}
          </div>
          {preferredPositions.length === 0 && (
            <p className="text-xs text-ink-500 mt-3">No preference set — Posturra will choose positions from clinical findings and access (current behavior).</p>
          )}
        </div>

        <div className="flex items-center justify-end">
          <button
            data-testid="save-profile-btn"
            disabled={saving}
            className="min-h-[48px] px-6 rounded-full bg-sage-500 hover:bg-sage-600 text-white font-medium inline-flex items-center gap-2 disabled:opacity-50"
          >
            <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save profile"}
          </button>
        </div>
      </form>

      {/* Referral / Share */}
      <div className="mt-10 max-w-3xl bg-sage-500 text-white rounded-3xl p-6 sm:p-8" data-testid="referral-block">
        <div className="flex items-center gap-2 mb-2 opacity-90">
          <Share2 className="w-4 h-4" />
          <div className="text-xs uppercase tracking-[0.16em]">Refer a colleague</div>
        </div>
        <h2 className="font-heading text-2xl sm:text-3xl font-light tracking-tight mb-3">Help other therapists save hours per week.</h2>
        <p className="text-white/80 max-w-xl mb-5">
          Share Posturra with another therapist. When they sign up using your link, we&rsquo;ll credit it to you — and you&rsquo;ll see it counted here.
        </p>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex-1 min-w-[260px] bg-white/10 rounded-xl px-4 py-3 text-sm font-mono break-all border border-white/10" data-testid="referral-link">
            {referralLink || "—"}
          </div>
          <button
            data-testid="copy-link-btn"
            onClick={copyLink}
            className="min-h-[44px] px-5 rounded-full bg-white text-sage-700 font-medium inline-flex items-center gap-2 hover:bg-paper-50 transition-all"
          >
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            {copied ? "Copied" : "Copy"}
          </button>
          <a
            data-testid="share-email-btn"
            href={`mailto:?subject=${encodeURIComponent('Try Posturra — AI posture + SOAP for massage therapists')}&body=${shareMessage}`}
            className="min-h-[44px] px-5 rounded-full bg-ink-900/30 hover:bg-ink-900/40 text-white font-medium inline-flex items-center gap-2 backdrop-blur-sm transition-all"
          >
            Email a colleague
          </a>
          <a
            data-testid="share-sms-btn"
            href={`sms:?body=${shareMessage}`}
            className="min-h-[44px] px-5 rounded-full bg-ink-900/30 hover:bg-ink-900/40 text-white font-medium inline-flex items-center gap-2 backdrop-blur-sm transition-all"
          >
            Text it
          </a>
        </div>
        <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 text-sm">
          <span className="font-heading text-lg" data-testid="referrals-count">{me?.referrals_count ?? 0}</span>
          <span className="text-white/80">{me?.referrals_count === 1 ? "colleague" : "colleagues"} joined via your link</span>
        </div>
      </div>

      <div className="mt-6 text-xs text-ink-500">Account: {user?.email}</div>
    </Layout>
  );
}
